import React from 'react';
import {
  ClipboardCheck,
  Database,
  GitCompare,
  Package,
  Search,
  ShieldCheck,
  Warehouse,
} from 'lucide-react';
import MatchDistribution from './MatchDistribution';
import SectionPanel from './SectionPanel';
import StatCard from './StatCard';
import { fmt, fmtDate } from './formatters';

function StatusPill({ status, statusBadgeClass }) {
  if (!status || status === '—') return <span className="text-muted">—</span>;
  return <span className={statusBadgeClass(status)}>{status}</span>;
}

function QualityScoreCard({ score }) {
  const numeric = score != null ? Math.max(0, Math.min(100, Number(score))) : null;
  return (
    <div className="col-12 col-lg-4">
      <div className="ahri-dash-quality h-100">
        <div
          className="ahri-dash-quality__ring mb-2"
          style={{ '--score': numeric ?? 0 }}
          aria-hidden
        >
          <span className="ahri-dash-quality__score">{numeric != null ? `${numeric}%` : '—'}</span>
        </div>
        <div className="fw-semibold text-dark">Catalog quality score</div>
        <div className="small text-muted">Based on missing-field analysis</div>
      </div>
    </div>
  );
}

function DashboardDetails({
  ahri,
  ahriSync,
  ahriBreakdown,
  matchTotals,
  ahriSolr,
  mappingJob,
  productDb,
  productIndex,
  indexState,
  productV4,
  inventory,
  dq,
  dqSummary,
  dqLast,
  statusBadgeClass,
}) {
  const v4 = productV4 || {};
  const hasV4 = Boolean(v4 && v4.job_id);
  return (
    <>
      <details className="ahri-dash-details mb-4">
        <summary className="ahri-dash-details__toggle btn btn-outline-secondary btn-sm">
          Show step metrics &amp; matchup details
        </summary>

        <div className="ahri-dash-details__body mt-3">
          <SectionPanel
            title="AHRI program data"
            description="Records stored after AHRI GraphQL sync"
            linkTo="/ahri"
            linkLabel="Manage sync"
            tone="primary"
            icon={Database}
          >
            <div className="row g-3">
              <StatCard label="Total systems" value={fmt(ahri.total_count)} />
              <StatCard
                label="Last sync"
                value={fmtDate(ahriSync.finished_at || ahriSync.started_at)}
                hint={ahriSync.status ? <StatusPill status={ahriSync.status} statusBadgeClass={statusBadgeClass} /> : ''}
              />
              <StatCard label="New (last job)" value={fmt(ahriBreakdown.new)} tone="success" />
              <StatCard label="Updated (last job)" value={fmt(ahriBreakdown.updated)} tone="info" />
              <StatCard label="Upserted (last job)" value={fmt(ahriSync.items_upserted)} hint="Rows written" />
            </div>
          </SectionPanel>

          <div className="row g-0 g-lg-4">
            <div className="col-12 col-xl-8">
              <SectionPanel
                title="AHRI ↔ product matchup"
                description="How certified systems map to your catalog"
                linkTo="/ahri"
                linkLabel="Run mapping"
                tone="rose"
                icon={GitCompare}
              >
                <MatchDistribution totals={matchTotals} />
                <div className="row g-3">
                  <StatCard
                    label="Last mapping"
                    value={fmtDate(mappingJob.finished_at || mappingJob.started_at)}
                    hint={mappingJob.status ? <StatusPill status={mappingJob.status} statusBadgeClass={statusBadgeClass} /> : ''}
                  />
                  <StatCard label="Matches (last job)" value={fmt(mappingJob.matches)} tone="info" />
                  <StatCard label="Products updated" value={fmt(mappingJob.updated_products)} />
                  <StatCard label="Linked products" value={fmt(productDb.with_ahri)} tone="success" />
                  <StatCard label="Without AHRI" value={fmt(productDb.without_ahri)} />
                </div>
              </SectionPanel>
            </div>
            <div className="col-12 col-xl-4">
              <SectionPanel
                title="AHRI search (Solr)"
                description="Indexed for System Shop & facets"
                linkTo="/ahri/solr"
                linkLabel="Solr console"
                tone="indigo"
                icon={Search}
              >
                <div className="row g-3">
                  <StatCard label="Status" value={<StatusPill status={ahriSolr.status} statusBadgeClass={statusBadgeClass} />} compact />
                  <StatCard
                    label="Last indexed"
                    value={fmtDate(ahriSolr.last_synced_at)}
                    hint={ahriSolr.last_mode ? `Mode: ${ahriSolr.last_mode}` : ''}
                    compact
                  />
                  <StatCard label="Docs (last run)" value={fmt(ahriSolr.documents_indexed)} compact />
                  <StatCard label="Account docs" value={fmt(ahriSolr.doc_counts?.account_docs)} compact />
                </div>
              </SectionPanel>
            </div>
          </div>

          <SectionPanel
            title="Product catalog"
            description="Documents v4 imports drive product data sync; Solr / OpenSearch are kept in sync downstream"
            linkTo="/v4-documents"
            linkLabel="V4 imports"
            tone="emerald"
            icon={Package}
          >
            <div className="row g-3">
              <StatCard label="Total products" value={fmt(productDb.total)} />
              <StatCard
                label="Last V4 import"
                value={fmtDate(v4.finished_at || v4.started_at)}
                hint={hasV4 ? (
                  <StatusPill status={v4.status} statusBadgeClass={statusBadgeClass} />
                ) : 'No import yet'}
              />
              <StatCard
                label="Source file"
                value={v4.file_name ? <code>{v4.file_name}</code> : '—'}
                hint={v4.import_mode ? `Mode: ${v4.import_mode}` : ''}
              />
              <StatCard label="New (last import)" value={fmt(v4.new)} tone="success" />
              <StatCard label="Updated (last import)" value={fmt(v4.updated)} tone="info" />
              <StatCard
                label="Deleted (last import)"
                value={v4.deleted_supported ? fmt(v4.deleted) : 'n/a'}
                hint={v4.deleted_supported ? '' : 'V4 import is upsert-only'}
              />
              <StatCard
                label="Rows processed"
                value={fmt(v4.processed_rows)}
                hint={v4.total_rows ? `of ${fmt(v4.total_rows)} parsed` : ''}
              />
              <StatCard
                label="Import errors"
                value={fmt(v4.errors_count)}
                tone={v4.errors_count ? 'warning' : 'default'}
              />
              <StatCard label="Search engine" value={fmt(productIndex.primary_engine)} tone="info" />
              <StatCard
                label="Last index sync"
                value={fmtDate(indexState?.last_synced_at || indexState?.finished_at)}
                hint={indexState?.mode ? `Index mode: ${indexState.mode}` : ''}
              />
            </div>
          </SectionPanel>

          <div className="row g-0 g-lg-4">
            <div className="col-12 col-lg-7">
              <SectionPanel
                title="Inventory sync"
                description="Warehouse availability into AHRI & products"
                linkTo="/ahri"
                linkLabel="Import"
                tone="amber"
                icon={Warehouse}
              >
                <div className="row g-3">
                  <StatCard
                    label="Last import"
                    value={fmtDate(inventory.completed_at || inventory.started_at)}
                    hint={inventory.mode ? `Mode: ${inventory.mode}` : ''}
                  />
                  <StatCard label="Status" value={<StatusPill status={inventory.status} statusBadgeClass={statusBadgeClass} />} />
                  <StatCard label="Rows parsed" value={fmt(inventory.rows_parsed)} />
                  <StatCard label="Snapshot updated" value={fmt(inventory.snapshot_rows_updated)} tone="info" />
                  <StatCard label="Parts changed" value={fmt(inventory.changed_parts)} />
                  <StatCard label="AHRI impacted" value={fmt(inventory.impacted_ahri_systems)} tone="warning" />
                </div>
              </SectionPanel>
            </div>
            <div className="col-12 col-lg-5">
              <SectionPanel
                title="Data quality"
                description="AI standardization & field hygiene"
                linkTo="/data-quality"
                linkLabel="Open DQ"
                tone="violet"
                icon={ShieldCheck}
              >
                <div className="row g-3 align-items-stretch">
                  <QualityScoreCard score={dqSummary.quality_score} />
                  <div className="col-12 col-lg-8">
                    <div className="row g-3">
                      <StatCard label="AI-standardized" value={fmt(productDb.ai_standardized)} tone="success" compact />
                      <StatCard
                        label="Last run"
                        value={dqLast?.created_at ? fmtDate(dqLast.created_at * 1000) : '—'}
                        hint={dqLast?.type || 'Session jobs only'}
                        compact
                      />
                      <StatCard label="Run status" value={<StatusPill status={dqLast?.status} statusBadgeClass={statusBadgeClass} />} compact />
                      <StatCard
                        label="Updates applied"
                        value={fmt(dqLast?.updates_applied ?? dqLast?.summary?.ok_count)}
                        compact
                      />
                    </div>
                  </div>
                </div>
              </SectionPanel>
            </div>
          </div>

          {dq.recent_runs?.length ? (
            <SectionPanel
              title="Recent data quality jobs"
              description="In-memory jobs for this server session"
              tone="violet"
              icon={ClipboardCheck}
            >
              <div className="table-responsive rounded-3 border">
                <table className="table table-hover ahri-dash-table mb-0">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Status</th>
                      <th>When</th>
                      <th>Progress</th>
                      <th className="text-end">Updates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dq.recent_runs.map((run) => (
                      <tr key={run.id}>
                        <td className="fw-medium">{run.type}</td>
                        <td><StatusPill status={run.status} statusBadgeClass={statusBadgeClass} /></td>
                        <td className="text-muted">{fmtDate((run.created_at || 0) * 1000)}</td>
                        <td>{fmt(run.current)} / {fmt(run.total)}</td>
                        <td className="text-end fw-semibold">
                          {fmt(run.updates_applied ?? run.summary?.ok_count)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionPanel>
          ) : null}
        </div>
      </details>
    </>
  );
}

export default DashboardDetails;
